import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import { useMutation } from "@apollo/client";
import type { PageContentRow, PageContent } from "../pages/types";
import {
  sectionRegistry,
  type SectionType,
} from "@sections/registry";
import {
  UPDATE_PAGE,
  PUBLISH_PAGE,
  REVERT_PAGE_DRAFT,
  evictPageListings,
} from "../pages/mutations";
import { useUndoRedo } from "./useUndoRedo";
import { pickDefaultBackground } from "./pickDefaultBackground";

export type Viewport = "desktop" | "tablet" | "mobile";

export interface EditorState {
  content: PageContentRow[];
  isDirty: boolean;
  selectedSectionId: string | null;
  viewport: Viewport;
  isSaving: boolean;
  isPublishing: boolean;
  isReverting: boolean;

  // Mutations
  updateSection: (id: string, props: Record<string, unknown>) => void;
  updateSectionProp: (id: string, path: string, value: unknown) => void;
  /** Update section without pushing to undo (for per-keystroke inline edits). */
  updateSectionSilent: (id: string, props: Record<string, unknown>) => void;
  /** Update a nested section prop without pushing to undo. */
  updateSectionPropSilent: (id: string, path: string, value: unknown) => void;
  /** Push current content as an undo checkpoint (call after a silent-edit session). */
  commitUndoCheckpoint: () => void;
  addSection: (
    type: string,
    props: Record<string, unknown>,
    atIndex?: number,
  ) => string;
  removeSection: (id: string) => void;
  moveSection: (id: string, direction: "up" | "down") => void;
  reorderSection: (fromIndex: number, toIndex: number) => void;
  toggleSectionVisibility: (id: string) => void;
  duplicateSection: (id: string) => void;
  setContent: (newContent: PageContentRow[]) => void;
  selectSection: (id: string | null) => void;
  setViewport: (vp: Viewport) => void;

  // Item focus (canvas click-to-focus for repeatable items)
  focusItemRequest: { arrayProp: string; index: number } | null;
  setFocusItemRequest: (request: { arrayProp: string; index: number } | null) => void;

  // Undo / redo
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;

  // Persistence
  saveDraft: () => Promise<{ success: boolean; error?: string }>;
  publish: () => Promise<{ success: boolean; error?: string }>;
  revertToPublished: () => Promise<{
    success: boolean;
    error?: string;
    newContent?: PageContentRow[];
  }>;
}

function generateSectionId(): string {
  return `section-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function parseContent(
  raw: string | null | undefined,
  rootRef?: MutableRefObject<Record<string, unknown>>,
): PageContentRow[] {
  if (!raw) return [];
  try {
    const parsed: PageContent = JSON.parse(raw);
    if (rootRef && parsed.root) {
      rootRef.current = parsed.root;
    }
    return (parsed.content || []).map((row, idx) => ({
      ...row,
      id: row.id || `section-${idx}`,
      enabled: row.enabled !== false,
    }));
  } catch {
    console.warn("[useEditorState] Failed to parse page content JSON");
    return [];
  }
}

function serializeContent(
  rows: PageContentRow[],
  root: Record<string, unknown>,
): string {
  const data: PageContent = { content: rows };
  if (Object.keys(root).length > 0) {
    data.root = root;
  }
  return JSON.stringify(data);
}

export function useEditorState(
  pageId: string,
  initialDraftContent: string | null | undefined,
): EditorState {
  // Preserve the top-level `root` field across parse/serialize round-trips
  const rootRef = useRef<Record<string, unknown>>({});

  const [content, setContentRaw] = useState<PageContentRow[]>(() =>
    parseContent(initialDraftContent, rootRef),
  );
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null,
  );
  const [viewport, setViewport] = useState<Viewport>("desktop");
  const [focusItemRequest, setFocusItemRequest] = useState<{
    arrayProp: string;
    index: number;
  } | null>(null);

  // Value-based dirty tracking: compare current serialized content to saved
  // baseline rather than latching a boolean on every edit. This means that
  // undo-ing back to the saved state correctly reports "not dirty".
  // savedRef holds the serialized form from the last save/publish/revert.
  // savedVersion is a counter that forces the isDirty useMemo to recompute
  // when the baseline changes (since refs don't trigger re-renders).
  const savedRef = useRef<string | null>(null);
  const [savedVersion, setSavedVersion] = useState(0);

  // Undo/redo stack (session-scoped -- clears on unmount via useEffect)
  const undoRedo = useUndoRedo();
  // Keep a ref to content for computing new state synchronously in mutations
  const contentRef = useRef(content);
  contentRef.current = content;

  // Derive isDirty by comparing current serialized content to saved baseline.
  // On first render, the baseline is established from the initial content.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isDirty = useMemo(() => {
    const current = serializeContent(content, rootRef.current);
    if (savedRef.current === null) {
      savedRef.current = current;
    }
    return current !== savedRef.current;
  }, [content, savedVersion]);

  // Track canUndo/canRedo as state so the UI re-renders on undo/redo
  const [canUndoState, setCanUndoState] = useState(false);
  const [canRedoState, setCanRedoState] = useState(false);

  const refreshUndoRedoFlags = useCallback(() => {
    setCanUndoState(undoRedo.canUndo());
    setCanRedoState(undoRedo.canRedo());
  }, [undoRedo]);

  /**
   * Record new content as an undo checkpoint and update React state.
   * Every content-mutating operation goes through here.
   * (isDirty is derived automatically from the content vs saved baseline.)
   */
  const commitContent = useCallback(
    (next: PageContentRow[]) => {
      contentRef.current = next;
      undoRedo.push(next);
      setContentRaw(next);
      refreshUndoRedoFlags();
    },
    [undoRedo, refreshUndoRedoFlags],
  );

  /**
   * Update React state WITHOUT pushing to undo.
   * Used for per-keystroke inline text edits -- the caller commits
   * a single undo checkpoint when the editing session ends.
   */
  const commitContentSilent = useCallback((next: PageContentRow[]) => {
    contentRef.current = next;
    setContentRaw(next);
  }, []);

  /**
   * Push the current content as an undo checkpoint. Call this after
   * a batch of silent updates (e.g. when the inline editor closes).
   */
  const commitUndoCheckpoint = useCallback(() => {
    undoRedo.push(contentRef.current);
    refreshUndoRedoFlags();
  }, [undoRedo, refreshUndoRedoFlags]);

  // Record initial content as the undo baseline (the "original" state).
  // undoRedo is stable so this runs once on mount.
  useEffect(() => {
    undoRedo.push(contentRef.current);
    refreshUndoRedoFlags();
  }, [undoRedo, refreshUndoRedoFlags]);

  // Clear undo history on unmount
  useEffect(() => {
    return () => {
      undoRedo.clear();
    };
  }, [undoRedo]);

  const [updateMutation, updateResult] = useMutation(UPDATE_PAGE, {
    update: evictPageListings,
  });

  const [publishMutation, publishResult] = useMutation(PUBLISH_PAGE, {
    update: evictPageListings,
  });

  const [revertMutation, revertResult] = useMutation(REVERT_PAGE_DRAFT, {
    update: evictPageListings,
  });

  const setContent = useCallback(
    (newContent: PageContentRow[]) => {
      commitContent(newContent);
    },
    [commitContent],
  );

  const updateSection = useCallback(
    (id: string, props: Record<string, unknown>) => {
      const next = contentRef.current.map((row) =>
        row.id === id ? { ...row, props: { ...row.props, ...props } } : row,
      );
      commitContent(next);
    },
    [commitContent],
  );

  const updateSectionProp = useCallback(
    (id: string, path: string, value: unknown) => {
      const next = contentRef.current.map((row) => {
        if (row.id !== id) return row;
        const newProps = { ...row.props };
        const parts = path.split(".");
        // Navigate to the nested location and set the value
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let current: any = newProps;
        for (let i = 0; i < parts.length - 1; i++) {
          const key = parts[i];
          const nextKey = parts[i + 1];
          if (/^\d+$/.test(nextKey)) {
            // Next key is an array index -- clone the array
            current[key] = [...(current[key] as unknown[])];
            current = current[key];
          } else {
            // Next key is an object key -- clone the object
            current[key] = { ...current[key] };
            current = current[key];
          }
        }
        const lastKey = parts[parts.length - 1];
        current[lastKey] = value;
        return { ...row, props: newProps };
      });
      commitContent(next);
    },
    [commitContent],
  );

  /** Like updateSection but skips undo push (for per-keystroke inline edits). */
  const updateSectionSilent = useCallback(
    (id: string, props: Record<string, unknown>) => {
      const next = contentRef.current.map((row) =>
        row.id === id ? { ...row, props: { ...row.props, ...props } } : row,
      );
      commitContentSilent(next);
    },
    [commitContentSilent],
  );

  /** Like updateSectionProp but skips undo push. */
  const updateSectionPropSilent = useCallback(
    (id: string, path: string, value: unknown) => {
      const next = contentRef.current.map((row) => {
        if (row.id !== id) return row;
        const newProps = { ...row.props };
        const parts = path.split(".");
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let current: any = newProps;
        for (let i = 0; i < parts.length - 1; i++) {
          const key = parts[i];
          const nextKey = parts[i + 1];
          if (/^\d+$/.test(nextKey)) {
            current[key] = [...(current[key] as unknown[])];
            current = current[key];
          } else {
            current[key] = { ...current[key] };
            current = current[key];
          }
        }
        const lastKey = parts[parts.length - 1];
        current[lastKey] = value;
        return { ...row, props: newProps };
      });
      commitContentSilent(next);
    },
    [commitContentSilent],
  );

  const addSection = useCallback(
    (type: string, props: Record<string, unknown>, atIndex?: number): string => {
      // Merge the library's sparse props with the section's full defaults
      // so the new row has complete, renderable content.
      const mod = sectionRegistry[type as SectionType];
      const fullProps = mod ? { ...mod.defaults, ...props } : props;

      // Pick a context-aware background based on neighbors at the insertion point
      const current = contentRef.current;
      const insertIdx =
        atIndex !== undefined && atIndex >= 0 && atIndex <= current.length
          ? atIndex
          : current.length;
      const prevBg = insertIdx > 0
        ? (current[insertIdx - 1].props.backgroundStyle as string | undefined)
        : undefined;
      const nextBg = insertIdx < current.length
        ? (current[insertIdx].props.backgroundStyle as string | undefined)
        : undefined;
      fullProps.backgroundStyle = pickDefaultBackground(type, prevBg, nextBg);

      const newId = generateSectionId();
      const newRow: PageContentRow = {
        type,
        id: newId,
        enabled: true,
        props: fullProps,
      };
      let next: PageContentRow[];
      if (atIndex !== undefined && atIndex >= 0 && atIndex <= current.length) {
        next = [...current];
        next.splice(atIndex, 0, newRow);
      } else {
        next = [...current, newRow];
      }
      commitContent(next);
      return newId;
    },
    [commitContent],
  );

  const removeSection = useCallback(
    (id: string) => {
      const next = contentRef.current.filter((row) => row.id !== id);
      commitContent(next);
      setSelectedSectionId((prev) => (prev === id ? null : prev));
    },
    [commitContent],
  );

  const moveSection = useCallback(
    (id: string, direction: "up" | "down") => {
      const current = contentRef.current;
      const idx = current.findIndex((row) => row.id === id);
      if (idx < 0) return;
      const targetIdx = direction === "up" ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= current.length) return;
      const next = [...current];
      [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
      commitContent(next);
    },
    [commitContent],
  );

  const reorderSection = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;
      const current = contentRef.current;
      if (
        fromIndex < 0 ||
        fromIndex >= current.length ||
        toIndex < 0 ||
        toIndex >= current.length
      )
        return;
      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      commitContent(next);
    },
    [commitContent],
  );

  const toggleSectionVisibility = useCallback(
    (id: string) => {
      const next = contentRef.current.map((row) =>
        row.id === id ? { ...row, enabled: !row.enabled } : row,
      );
      commitContent(next);
    },
    [commitContent],
  );

  const duplicateSection = useCallback(
    (id: string) => {
      const current = contentRef.current;
      const idx = current.findIndex((row) => row.id === id);
      if (idx < 0) return;
      const source = current[idx];
      const dupe: PageContentRow = {
        ...source,
        id: generateSectionId(),
        props: { ...source.props },
      };
      const next = [...current];
      next.splice(idx + 1, 0, dupe);
      commitContent(next);
    },
    [commitContent],
  );

  const selectSection = useCallback((id: string | null) => {
    setSelectedSectionId(id);
  }, []);

  const undo = useCallback(() => {
    const snapshot = undoRedo.undo();
    if (snapshot) {
      contentRef.current = snapshot;
      setContentRaw(snapshot);
    }
    refreshUndoRedoFlags();
  }, [undoRedo, refreshUndoRedoFlags]);

  const redo = useCallback(() => {
    const snapshot = undoRedo.redo();
    if (snapshot) {
      contentRef.current = snapshot;
      setContentRaw(snapshot);
    }
    refreshUndoRedoFlags();
  }, [undoRedo, refreshUndoRedoFlags]);

  const saveDraft = useCallback(async () => {
    try {
      const serialized = serializeContent(content, rootRef.current);
      await updateMutation({
        variables: { id: pageId, draftContent: serialized },
      });
      savedRef.current = serialized;
      setSavedVersion((v) => v + 1);
      return { success: true };
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unknown error saving draft";
      return { success: false, error: message };
    }
  }, [content, pageId, updateMutation]);

  const publish = useCallback(async () => {
    try {
      // Save first if dirty
      if (isDirty) {
        const serialized = serializeContent(content, rootRef.current);
        await updateMutation({
          variables: { id: pageId, draftContent: serialized },
        });
        savedRef.current = serialized;
        setSavedVersion((v) => v + 1);
      }
      await publishMutation({ variables: { id: pageId } });
      return { success: true };
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unknown error publishing";
      return { success: false, error: message };
    }
  }, [content, isDirty, pageId, updateMutation, publishMutation]);

  const revertToPublished = useCallback(async () => {
    try {
      const result = await revertMutation({ variables: { id: pageId } });
      const reverted = result.data?.revertPageDraft;
      if (!reverted) throw new Error("Couldn't revert page");
      rootRef.current = {};
      const newContent = parseContent(
        (reverted as { draftContent?: string | null }).draftContent,
        rootRef,
      );
      setContentRaw(newContent);
      savedRef.current = serializeContent(newContent, rootRef.current);
      setSavedVersion((v) => v + 1);
      setSelectedSectionId(null);
      return { success: true, newContent };
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unknown error reverting";
      return { success: false, error: message };
    }
  }, [pageId, revertMutation]);

  return useMemo(
    () => ({
      content,
      isDirty,
      selectedSectionId,
      viewport,
      isSaving: updateResult.loading,
      isPublishing: publishResult.loading,
      isReverting: revertResult.loading,
      updateSection,
      updateSectionProp,
      updateSectionSilent,
      updateSectionPropSilent,
      commitUndoCheckpoint,
      addSection,
      removeSection,
      moveSection,
      reorderSection,
      toggleSectionVisibility,
      duplicateSection,
      setContent,
      selectSection,
      setViewport,
      focusItemRequest,
      setFocusItemRequest,
      undo,
      redo,
      canUndo: canUndoState,
      canRedo: canRedoState,
      saveDraft,
      publish,
      revertToPublished,
    }),
    [
      content,
      isDirty,
      selectedSectionId,
      viewport,
      updateResult.loading,
      publishResult.loading,
      revertResult.loading,
      updateSection,
      updateSectionProp,
      updateSectionSilent,
      updateSectionPropSilent,
      commitUndoCheckpoint,
      addSection,
      removeSection,
      moveSection,
      reorderSection,
      toggleSectionVisibility,
      duplicateSection,
      setContent,
      selectSection,
      setViewport,
      focusItemRequest,
      setFocusItemRequest,
      undo,
      redo,
      canUndoState,
      canRedoState,
      saveDraft,
      publish,
      revertToPublished,
    ],
  );
}
